import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getAdminPayouts,
  getPayoutDetails,
  processPayoutRequest,
  completePayoutRequest,
  rejectPayoutRequest,
  getPayoutStats,
  getPendingPayments,
  createPayoutFromBookings
} from './adminService';
import {
  Calendar,
  Clock,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  DollarSign,
  CreditCard,
  ChevronDown,
  ChevronUp,
  FileText,
  Send,
  X,
  Loader,
  Plus
} from 'lucide-react';

export default function AdminPaymentsPage() {
  const { isArabic } = useOutletContext();

  // Tab management
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'processing', 'completed'

  // Common states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Pending payments states
  const [pendingPayments, setPendingPayments] = useState([]);
  const [selectedTeacherPayment, setSelectedTeacherPayment] = useState(null);

  // Payouts states (for processing and completed tabs)
  const [payouts, setPayouts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPayout, setExpandedPayout] = useState(null);
  const [payoutDetails, setPayoutDetails] = useState({});

  // Modal states
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'complete', 'reject', 'create_payout'
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Load data on mount and tab change
  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Load appropriate data based on active tab
  async function loadData() {
    if (activeTab === 'pending') {
      await loadPendingPayments();
    } else {
      await loadPayouts();
    }
    await loadStats();
  }

  // Load pending payments (unpaid bookings)
  async function loadPendingPayments() {
    try {
      setLoading(true);
      setError(null);

      const result = await getPendingPayments(null, 50, 0);
      setPendingPayments(result.payments || []);
    } catch (err) {
      console.error('Error loading pending payments:', err);
      setError(err.message || 'Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  }

  // Load payouts with filters (for processing/completed tabs)
  async function loadPayouts() {
    try {
      setLoading(true);
      setError(null);

      // Determine which statuses to fetch based on tab
      let status = null;
      if (activeTab === 'processing') {
        status = 'pending'; // or 'processing' - we show both
      } else if (activeTab === 'completed') {
        status = 'completed';
      }

      const result = await getAdminPayouts(status);
      setPayouts(result.payouts || []);
    } catch (err) {
      console.error('Error loading payouts:', err);
      setError(err.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }

  // Load statistics
  async function loadStats() {
    try {
      const result = await getPayoutStats();
      setStats(result.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }

  // Load payout details when expanded
  async function loadPayoutDetails(payoutId) {
    if (payoutDetails[payoutId]) {
      return; // Already loaded
    }

    try {
      const result = await getPayoutDetails(payoutId);
      setPayoutDetails(prev => ({
        ...prev,
        [payoutId]: result.payout
      }));
    } catch (err) {
      console.error('Error loading payout details:', err);
      setError(err.message || 'Failed to load payout details');
    }
  }

  // Handle creating payout from pending payments
  async function handleCreatePayout(teacherPayment) {
    const bookingIds = teacherPayment.sessions.map(s => s.booking_id);

    try {
      setProcessingAction(true);
      await createPayoutFromBookings({
        teacher_email: teacherPayment.teacher_email,
        booking_ids: bookingIds,
        notes: `Created by admin on ${new Date().toLocaleDateString()}`
      });

      // Refresh pending payments
      await loadPendingPayments();
      await loadStats();

      // Show success message
      alert(isArabic
        ? `تم إنشاء الدفعة بنجاح للمعلم ${teacherPayment.teacher_name}`
        : `Payout created successfully for ${teacherPayment.teacher_name}`
      );

      // Switch to processing tab to show created payout
      setActiveTab('processing');
    } catch (err) {
      console.error('Error creating payout:', err);
      alert(err.message || 'Failed to create payout');
    } finally {
      setProcessingAction(false);
    }
  }

  // Handle payout expansion
  function handleToggleExpand(payoutId) {
    if (expandedPayout === payoutId) {
      setExpandedPayout(null);
    } else {
      setExpandedPayout(payoutId);
      loadPayoutDetails(payoutId);
    }
  }

  // Open action modal
  function openActionModal(payout, type) {
    setSelectedPayout(payout);
    setActionType(type);
    setShowActionModal(true);
    setActionNotes('');
    setRejectReason('');
  }

  // Close action modal
  function closeActionModal() {
    setShowActionModal(false);
    setSelectedPayout(null);
    setActionType(null);
    setActionNotes('');
    setRejectReason('');
  }

  // Handle process payout (mark as processing)
  async function handleProcessPayout(payout) {
    try {
      setProcessingAction(true);
      await processPayoutRequest(payout.id, '');
      await loadPayouts();
      setExpandedPayout(null);
    } catch (err) {
      console.error('Error processing payout:', err);
      alert(err.message || 'Failed to mark as processing');
    } finally {
      setProcessingAction(false);
    }
  }

  // Handle complete payout
  async function handleCompletePayout() {
    if (!selectedPayout) return;

    try {
      setProcessingAction(true);
      await completePayoutRequest(selectedPayout.id, actionNotes);
      await loadPayouts();
      await loadStats();
      closeActionModal();
      setExpandedPayout(null);

      alert(isArabic
        ? 'تم تأكيد الدفع بنجاح'
        : 'Payment confirmed successfully'
      );
    } catch (err) {
      console.error('Error completing payout:', err);
      alert(err.message || 'Failed to complete payout');
    } finally {
      setProcessingAction(false);
    }
  }

  // Handle reject payout
  async function handleRejectPayout() {
    if (!selectedPayout || !rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setProcessingAction(true);
      await rejectPayoutRequest(selectedPayout.id, rejectReason, actionNotes);
      await loadPayouts();
      await loadStats();
      closeActionModal();
      setExpandedPayout(null);
    } catch (err) {
      console.error('Error rejecting payout:', err);
      alert(err.message || 'Failed to reject payout');
    } finally {
      setProcessingAction(false);
    }
  }

  // Calculate hours since payment
  function getHoursSincePayment(paidAt) {
    const now = new Date();
    const paymentDate = new Date(paidAt);
    const diffMs = now - paymentDate;
    return Math.floor(diffMs / (1000 * 60 * 60));
  }

  // Check if payment is overdue (>24 hours)
  function isOverdue(paidAt) {
    return getHoursSincePayment(paidAt) > 24;
  }

  // Format currency
  function formatCurrency(amount) {
    return `${parseFloat(amount || 0).toFixed(3)} BD`;
  }

  // Get status badge
  function getStatusBadge(status) {
    const statusConfig = {
      pending: {
        label: isArabic ? 'قيد الانتظار' : 'Pending',
        bg: '#fff3e0',
        color: '#FF9800',
        icon: Clock
      },
      processing: {
        label: isArabic ? 'قيد المعالجة' : 'Processing',
        bg: '#e3f2fd',
        color: '#2196F3',
        icon: Loader
      },
      completed: {
        label: isArabic ? 'مكتمل' : 'Completed',
        bg: '#e8f5e9',
        color: '#4CAF50',
        icon: CheckCircle
      },
      cancelled: {
        label: isArabic ? 'ملغي' : 'Cancelled',
        bg: '#ffebee',
        color: '#f44336',
        icon: XCircle
      },
      failed: {
        label: isArabic ? 'فشل' : 'Failed',
        bg: '#ffebee',
        color: '#f44336',
        icon: AlertCircle
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span style={{
        padding: '6px 12px',
        background: config.bg,
        color: config.color,
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6
      }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  }

  // Filter payouts by search
  const filteredPayouts = payouts.filter(payout => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payout.teacher_email?.toLowerCase().includes(query) ||
      payout.teacher_id?.toLowerCase().includes(query)
    );
  });

  // Calculate pending payments count from pendingPayments array
  const pendingPaymentsCount = pendingPayments.reduce((sum, p) => sum + p.session_count, 0);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'إدارة مدفوعات المعلمين' : 'Teacher Payments Management'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'مراقبة ومعالجة مدفوعات المعلمين'
            : 'Monitor and process teacher payments'}
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <StatCard
          label={isArabic ? 'الجلسات المعلقة' : 'Pending Sessions'}
          value={pendingPaymentsCount}
          color="#FF9800"
          icon={Clock}
        />
        <StatCard
          label={isArabic ? 'قيد المعالجة' : 'Processing'}
          value={(stats?.pendingCount || 0) + (stats?.processingCount || 0)}
          color="#2196F3"
          icon={Loader}
        />
        <StatCard
          label={isArabic ? 'المكتملة' : 'Completed'}
          value={stats?.completedCount || 0}
          color="#4CAF50"
          icon={CheckCircle}
        />
        <StatCard
          label={isArabic ? 'إجمالي المدفوعات' : 'Total Paid'}
          value={formatCurrency(stats?.totalPaid || 0)}
          color="#9C27B0"
          icon={DollarSign}
        />
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        borderBottom: '2px solid #e0e0e0'
      }}>
        <TabButton
          active={activeTab === 'pending'}
          onClick={() => setActiveTab('pending')}
          isArabic={isArabic}
        >
          {isArabic ? 'دفعات معلقة' : 'Pending Payments'}
          {pendingPayments.length > 0 && (
            <span style={{
              marginLeft: 8,
              background: '#FF9800',
              color: 'white',
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 600
            }}>
              {pendingPayments.length}
            </span>
          )}
        </TabButton>

        <TabButton
          active={activeTab === 'processing'}
          onClick={() => setActiveTab('processing')}
          isArabic={isArabic}
        >
          {isArabic ? 'قيد المعالجة' : 'Processing'}
          {((stats?.pendingCount || 0) + (stats?.processingCount || 0)) > 0 && (
            <span style={{
              marginLeft: 8,
              background: '#2196F3',
              color: 'white',
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 600
            }}>
              {(stats?.pendingCount || 0) + (stats?.processingCount || 0)}
            </span>
          )}
        </TabButton>

        <TabButton
          active={activeTab === 'completed'}
          onClick={() => setActiveTab('completed')}
          isArabic={isArabic}
        >
          {isArabic ? 'مكتملة' : 'Completed'}
        </TabButton>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: 16,
          borderRadius: 12,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Loader size={48} className="spin" style={{ color: '#FF6B35', margin: '0 auto' }} />
          <p style={{ color: '#666', marginTop: 16 }}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      )}

      {/* Tab Content */}
      {!loading && (
        <>
          {activeTab === 'pending' && <PendingPaymentsTab
            pendingPayments={pendingPayments}
            isArabic={isArabic}
            onCreatePayout={handleCreatePayout}
            processingAction={processingAction}
            formatCurrency={formatCurrency}
            getHoursSincePayment={getHoursSincePayment}
            isOverdue={isOverdue}
          />}

          {activeTab === 'processing' && <ProcessingPayoutsTab
            payouts={filteredPayouts}
            isArabic={isArabic}
            expandedPayout={expandedPayout}
            payoutDetails={payoutDetails}
            onToggleExpand={handleToggleExpand}
            onProcess={handleProcessPayout}
            onComplete={(payout) => openActionModal(payout, 'complete')}
            onReject={(payout) => openActionModal(payout, 'reject')}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            processingAction={processingAction}
          />}

          {activeTab === 'completed' && <CompletedPayoutsTab
            payouts={filteredPayouts}
            isArabic={isArabic}
            expandedPayout={expandedPayout}
            payoutDetails={payoutDetails}
            onToggleExpand={handleToggleExpand}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />}
        </>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <ActionModal
          isArabic={isArabic}
          actionType={actionType}
          selectedPayout={selectedPayout}
          actionNotes={actionNotes}
          setActionNotes={setActionNotes}
          rejectReason={rejectReason}
          setRejectReason={setRejectReason}
          processingAction={processingAction}
          onConfirm={actionType === 'complete' ? handleCompletePayout : handleRejectPayout}
          onClose={closeActionModal}
          formatCurrency={formatCurrency}
        />
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ============= SUB-COMPONENTS =============

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{
      background: 'white',
      padding: 20,
      borderRadius: 16,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ margin: 0, color: '#666', fontSize: 13, marginBottom: 8 }}>{label}</p>
          <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#232B3E' }}>{value}</p>
        </div>
        <div style={{
          background: color + '20',
          color: color,
          padding: 12,
          borderRadius: 12
        }}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, isArabic }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '12px 24px',
        border: 'none',
        background: 'transparent',
        color: active ? '#FF6B35' : '#666',
        fontWeight: active ? 600 : 400,
        fontSize: 15,
        cursor: 'pointer',
        borderBottom: active ? '3px solid #FF6B35' : '3px solid transparent',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {children}
    </button>
  );
}

// Pending Payments Tab Component
function PendingPaymentsTab({
  pendingPayments,
  isArabic,
  onCreatePayout,
  processingAction,
  formatCurrency,
  getHoursSincePayment,
  isOverdue
}) {
  if (pendingPayments.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <CheckCircle size={64} style={{ color: '#4CAF50', margin: '0 auto 16px' }} />
        <h3 style={{ color: '#666', fontSize: 18 }}>
          {isArabic ? 'لا توجد دفعات معلقة' : 'No Pending Payments'}
        </h3>
        <p style={{ color: '#999', fontSize: 14 }}>
          {isArabic
            ? 'جميع المدفوعات قد تمت معالجتها'
            : 'All payments have been processed'}
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {pendingPayments.map((payment, index) => {
        const overdue = isOverdue(payment.oldest_payment);
        const hoursSince = getHoursSincePayment(payment.oldest_payment);

        return (
          <div key={index} style={{
            background: 'white',
            border: overdue ? '2px solid #f44336' : '2px solid #e0e0e0',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: 20, color: '#232B3E' }}>
                  {payment.teacher_name || isArabic ? 'معلم' : 'Teacher'}
                </h3>
                <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                  {payment.teacher_email}
                </p>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#4CAF50' }}>
                  {formatCurrency(payment.total_earnings)}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {payment.session_count} {isArabic ? 'جلسة' : 'session'}
                  {payment.session_count > 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* Deadline Warning */}
            {overdue && (
              <div style={{
                background: '#ffebee',
                color: '#c62828',
                padding: 12,
                borderRadius: 8,
                marginBottom: 16,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <AlertCircle size={16} />
                {isArabic
                  ? `متأخر ${hoursSince} ساعة - يجب الدفع خلال 24 ساعة`
                  : `Overdue by ${hoursSince - 24} hours - Payment required within 24 hours`}
              </div>
            )}

            {/* Sessions Table */}
            <div style={{ marginBottom: 20, overflowX: 'auto' }}>
              <h4 style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                {isArabic ? 'تفاصيل الجلسات' : 'Session Details'}:
              </h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: 12, textAlign: 'left' }}>
                      {isArabic ? 'الطالب' : 'Student'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'left' }}>
                      {isArabic ? 'الموضوع' : 'Subject'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'left' }}>
                      {isArabic ? 'التاريخ والوقت' : 'Date & Time'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'left' }}>
                      {isArabic ? 'النوع' : 'Type'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'right' }}>
                      {isArabic ? 'دفع الطالب' : 'Student Paid'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'right' }}>
                      {isArabic ? 'عمولة المنصة' : 'Platform Fee'}
                    </th>
                    <th style={{ padding: 12, textAlign: 'right' }}>
                      {isArabic ? 'ربح المعلم' : 'Teacher Earning'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {payment.sessions.map((session, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{session.student_name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{session.student_email || 'N/A'}</div>
                      </td>
                      <td style={{ padding: 12 }}>{session.subject}</td>
                      <td style={{ padding: 12 }}>
                        <div>{session.date}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {session.time_from} - {session.time_to}
                        </div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          background: session.lesson_format === 'online' ? '#e3f2fd' : '#fff3e0',
                          color: session.lesson_format === 'online' ? '#1976d2' : '#f57c00'
                        }}>
                          {session.lesson_format === 'online'
                            ? (isArabic ? 'عبر الإنترنت' : 'Online')
                            : (isArabic ? 'وجهاً لوجه' : 'In-Person')
                          }
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(session.student_paid_amount)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600, color: '#9C27B0' }}>
                        {formatCurrency(session.platform_commission)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600, color: '#4CAF50' }}>
                        {formatCurrency(session.teacher_earning)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f9f9f9', borderTop: '2px solid #e0e0e0' }}>
                    <td colSpan="4" style={{ padding: 12, fontWeight: 600 }}>
                      {isArabic ? 'المجموع' : 'TOTAL'} ({payment.session_count} {isArabic ? 'جلسة' : 'session'}{payment.session_count > 1 ? 's' : ''})
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>
                      {formatCurrency(payment.sessions.reduce((sum, s) => sum + (s.student_paid_amount || 0), 0))}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#9C27B0' }}>
                      {formatCurrency(payment.total_commission)}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#4CAF50' }}>
                      {formatCurrency(payment.total_earnings)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Action Button */}
            <button
              onClick={() => onCreatePayout(payment)}
              disabled={processingAction}
              style={{
                width: '100%',
                padding: 14,
                background: overdue ? '#f44336' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: processingAction ? 'not-allowed' : 'pointer',
                opacity: processingAction ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              {processingAction ? (
                <>
                  <Loader size={16} className="spin" />
                  {isArabic ? 'جاري الإنشاء...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus size={16} />
                  {isArabic ? 'إنشاء دفعة' : 'Create Payout'}
                </>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Processing Payouts Tab Component
function ProcessingPayoutsTab({
  payouts,
  isArabic,
  expandedPayout,
  payoutDetails,
  onToggleExpand,
  onProcess,
  onComplete,
  onReject,
  formatCurrency,
  getStatusBadge,
  searchQuery,
  setSearchQuery,
  processingAction
}) {
  // Filter only pending/processing statuses
  const processingPayouts = payouts.filter(p =>
    p.payout_status === 'pending' || p.payout_status === 'processing'
  );

  if (processingPayouts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <CheckCircle size={64} style={{ color: '#4CAF50', margin: '0 auto 16px' }} />
        <h3 style={{ color: '#666', fontSize: 18 }}>
          {isArabic ? 'لا توجد دفعات قيد المعالجة' : 'No Payouts Processing'}
        </h3>
        <p style={{ color: '#999', fontSize: 14 }}>
          {isArabic
            ? 'انتقل إلى "دفعات معلقة" لإنشاء دفعات جديدة'
            : 'Go to "Pending Payments" to create new payouts'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Search Bar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999'
          }} />
          <input
            type="text"
            placeholder={isArabic ? 'البحث بالبريد الإلكتروني للمعلم...' : 'Search by teacher email...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 48px',
              border: '2px solid #e0e0e0',
              borderRadius: 12,
              fontSize: 14,
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Payouts List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {processingPayouts.map(payout => (
          <PayoutCard
            key={payout.id}
            payout={payout}
            isArabic={isArabic}
            expanded={expandedPayout === payout.id}
            details={payoutDetails[payout.id]}
            onToggleExpand={() => onToggleExpand(payout.id)}
            onProcess={() => onProcess(payout)}
            onComplete={() => onComplete(payout)}
            onReject={() => onReject(payout)}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
            processingAction={processingAction}
            showActions={true}
          />
        ))}
      </div>
    </>
  );
}

// Completed Payouts Tab Component
function CompletedPayoutsTab({
  payouts,
  isArabic,
  expandedPayout,
  payoutDetails,
  onToggleExpand,
  formatCurrency,
  getStatusBadge,
  searchQuery,
  setSearchQuery
}) {
  const completedPayouts = payouts.filter(p => p.payout_status === 'completed');

  if (completedPayouts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <FileText size={64} style={{ color: '#999', margin: '0 auto 16px' }} />
        <h3 style={{ color: '#666', fontSize: 18 }}>
          {isArabic ? 'لا توجد دفعات مكتملة' : 'No Completed Payouts'}
        </h3>
      </div>
    );
  }

  return (
    <>
      {/* Search Bar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#999'
          }} />
          <input
            type="text"
            placeholder={isArabic ? 'البحث بالبريد الإلكتروني للمعلم...' : 'Search by teacher email...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 48px',
              border: '2px solid #e0e0e0',
              borderRadius: 12,
              fontSize: 14,
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Payouts List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {completedPayouts.map(payout => (
          <PayoutCard
            key={payout.id}
            payout={payout}
            isArabic={isArabic}
            expanded={expandedPayout === payout.id}
            details={payoutDetails[payout.id]}
            onToggleExpand={() => onToggleExpand(payout.id)}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
            showActions={false}
          />
        ))}
      </div>
    </>
  );
}

// Payout Card Component (Shared)
function PayoutCard({
  payout,
  isArabic,
  expanded,
  details,
  onToggleExpand,
  onProcess,
  onComplete,
  onReject,
  formatCurrency,
  getStatusBadge,
  processingAction,
  showActions
}) {
  return (
    <div style={{
      background: 'white',
      border: '2px solid #e0e0e0',
      borderRadius: 16,
      overflow: 'hidden'
    }}>
      {/* Card Header */}
      <div
        onClick={onToggleExpand}
        style={{
          padding: 20,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#232B3E' }}>
              {payout.teacher_email}
            </h3>
            {getStatusBadge(payout.payout_status)}
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>
            {payout.session_count} {isArabic ? 'جلسة' : 'session'}
            {payout.session_count > 1 ? 's' : ''} •{' '}
            {isArabic ? 'تاريخ الطلب' : 'Requested'}: {new Date(payout.requested_at).toLocaleDateString()}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#4CAF50' }}>
              {formatCurrency(payout.total_amount)}
            </div>
          </div>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && details && (
        <div style={{ borderTop: '2px solid #f5f5f5', padding: 20 }}>
          {/* Sessions Table */}
          {details.sessions && details.sessions.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                {isArabic ? 'تفاصيل الجلسات' : 'Session Details'}:
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>
                        {isArabic ? 'الطالب' : 'Student'}
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>
                        {isArabic ? 'الموضوع' : 'Subject'}
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>
                        {isArabic ? 'التاريخ' : 'Date'}
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>
                        {isArabic ? 'الوقت' : 'Time'}
                      </th>
                      <th style={{ padding: 12, textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>
                        {isArabic ? 'الربح' : 'Earning'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.sessions.map((session, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 12 }}>{session.student_name}</td>
                        <td style={{ padding: 12 }}>{session.subject}</td>
                        <td style={{ padding: 12 }}>{session.session_date}</td>
                        <td style={{ padding: 12 }}>{session.time_from} - {session.time_to}</td>
                        <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(session.teacher_earning)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bank Details */}
          {payout.bank_account_details && (
            <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 12, marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                {isArabic ? 'تفاصيل البنك' : 'Bank Details'}:
              </h4>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                <div><strong>{isArabic ? 'اسم الحساب' : 'Account Name'}:</strong> {payout.bank_account_details.account_name}</div>
                <div><strong>{isArabic ? 'رقم الحساب' : 'Account Number'}:</strong> {payout.bank_account_details.account_number}</div>
                <div><strong>{isArabic ? 'البنك' : 'Bank'}:</strong> {payout.bank_account_details.bank_name}</div>
                {payout.bank_account_details.iban && (
                  <div><strong>IBAN:</strong> {payout.bank_account_details.iban}</div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {payout.notes && (
            <div style={{ background: '#fff3cd', padding: 16, borderRadius: 12, marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, color: '#856404', marginBottom: 8 }}>
                {isArabic ? 'ملاحظات' : 'Notes'}:
              </h4>
              <p style={{ margin: 0, fontSize: 13, color: '#856404' }}>{payout.notes}</p>
            </div>
          )}

          {/* Action Buttons */}
          {showActions && (
            <div style={{ display: 'flex', gap: 12 }}>
              {payout.payout_status === 'pending' && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onProcess(); }}
                    disabled={processingAction}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processingAction ? 'not-allowed' : 'pointer',
                      opacity: processingAction ? 0.6 : 1
                    }}
                  >
                    {isArabic ? 'بدء المعالجة' : 'Start Processing'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onComplete(); }}
                    disabled={processingAction}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processingAction ? 'not-allowed' : 'pointer',
                      opacity: processingAction ? 0.6 : 1
                    }}
                  >
                    {isArabic ? 'تأكيد الدفع' : 'Confirm Payment'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onReject(); }}
                    disabled={processingAction}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processingAction ? 'not-allowed' : 'pointer',
                      opacity: processingAction ? 0.6 : 1
                    }}
                  >
                    {isArabic ? 'رفض' : 'Reject'}
                  </button>
                </>
              )}

              {payout.payout_status === 'processing' && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onComplete(); }}
                    disabled={processingAction}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processingAction ? 'not-allowed' : 'pointer',
                      opacity: processingAction ? 0.6 : 1
                    }}
                  >
                    {isArabic ? 'تأكيد الدفع' : 'Confirm Payment'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onReject(); }}
                    disabled={processingAction}
                    style={{
                      flex: 1,
                      padding: 12,
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: processingAction ? 'not-allowed' : 'pointer',
                      opacity: processingAction ? 0.6 : 1
                    }}
                  >
                    {isArabic ? 'رفض' : 'Reject'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Action Modal Component
function ActionModal({
  isArabic,
  actionType,
  selectedPayout,
  actionNotes,
  setActionNotes,
  rejectReason,
  setRejectReason,
  processingAction,
  onConfirm,
  onClose,
  formatCurrency
}) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        maxWidth: 500,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          padding: 24,
          borderBottom: '2px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#232B3E' }}>
            {actionType === 'complete'
              ? (isArabic ? 'تأكيد الدفع' : 'Confirm Payment')
              : (isArabic ? 'رفض الدفعة' : 'Reject Payout')
            }
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4
            }}
          >
            <X size={24} color="#666" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 20 }}>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>
              {isArabic ? 'المعلم' : 'Teacher'}:
            </p>
            <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
              {selectedPayout?.teacher_email}
            </p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>
              {isArabic ? 'المبلغ' : 'Amount'}:
            </p>
            <p style={{ fontSize: 24, fontWeight: 700, color: '#4CAF50', margin: 0 }}>
              {formatCurrency(selectedPayout?.total_amount)}
            </p>
          </div>

          {actionType === 'reject' && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
                {isArabic ? 'سبب الرفض' : 'Rejection Reason'} *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={isArabic ? 'أدخل سبب الرفض...' : 'Enter rejection reason...'}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '2px solid #e0e0e0',
                  borderRadius: 12,
                  fontSize: 14,
                  minHeight: 100,
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600 }}>
              {isArabic ? 'ملاحظات' : 'Notes'} {actionType === 'complete' && '(' + (isArabic ? 'اختياري' : 'Optional') + ')'}
            </label>
            <textarea
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              placeholder={
                actionType === 'complete'
                  ? (isArabic ? 'رقم مرجع الدفع، الملاحظات الداخلية، إلخ...' : 'Payment reference, internal notes, etc...')
                  : (isArabic ? 'ملاحظات إضافية...' : 'Additional notes...')
              }
              style={{
                width: '100%',
                padding: 12,
                border: '2px solid #e0e0e0',
                borderRadius: 12,
                fontSize: 14,
                minHeight: 80,
                resize: 'vertical',
                outline: 'none'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              disabled={processingAction}
              style={{
                flex: 1,
                padding: 12,
                background: '#f5f5f5',
                color: '#232B3E',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: processingAction ? 'not-allowed' : 'pointer'
              }}
            >
              {isArabic ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              onClick={onConfirm}
              disabled={processingAction || (actionType === 'reject' && !rejectReason.trim())}
              style={{
                flex: 1,
                padding: 12,
                background: actionType === 'complete' ? '#4CAF50' : '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                cursor: (processingAction || (actionType === 'reject' && !rejectReason.trim())) ? 'not-allowed' : 'pointer',
                opacity: (processingAction || (actionType === 'reject' && !rejectReason.trim())) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              {processingAction ? (
                <>
                  <Loader size={16} className="spin" />
                  {isArabic ? 'جاري المعالجة...' : 'Processing...'}
                </>
              ) : (
                actionType === 'complete'
                  ? (isArabic ? 'تأكيد الدفع' : 'Confirm Payment')
                  : (isArabic ? 'رفض الدفعة' : 'Reject Payout')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
